import { Heading } from '../../typography/heading/Heading';
import { Text } from '../../typography/text/Text';
import { Image } from '../../atoms/image/Image';
import styles from './PromoTile.module.scss';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import React, { useRef, useState, useCallback, useLayoutEffect, useMemo } from 'react';
import { Link } from '../../atoms/link/Link';
import { titleTextProps, subTitleTextProps, smallTextProps } from './_vars';
import { useMedia } from 'react-use';
import CustomVideoControls from '../../organisms/customVideoControls/CustomVideoControls';

import { useInView } from 'react-intersection-observer';

const isVideo = (link) => link.endsWith('.mp4') || link.endsWith('.webm');

const PromoTile = ({
  title = '',
  text = '',
  image = '',
  video = '',
  isClickable = false,
  smallText = '',
  layout: gridLayout = null,
  gridPosition,
  cellSpan,
  backgroundColor = 'grey',
  isBackgroundImage = false,
  promoLink,
  textStyles,
  productHeight,
  preloadImage = false,
}) => {
  const isMobile = useMedia('(max-width: 640px)', false);
  const [contentMaxHeight, setContentMaxHeight] = useState(0);
  const promoWrapperRef = useRef(null);
  const wrapperStyles = `${styles.imagelink}`;

  const { ref, inView } = useInView();

  const layout =
    isMobile && gridLayout === 4 && cellSpan === 1 ? 2 : gridLayout;

  const promoWritings = (
    <div
      className={clsx(
        styles.textBlock,
        styles[`title_layout__${layout}__span__${cellSpan}`],
      )}
    >
      <Heading className={styles.titleText} {...titleTextProps(backgroundColor && !isBackgroundImage && !image)}>
        {title}
      </Heading>
      <Heading {...subTitleTextProps} className={styles.subTitle}>
        {text}
      </Heading>
      <Text {...smallTextProps} className={styles.smallText}>
        {smallText}
      </Text>
    </div>
  );

  const PromoWrapper = ({ children }) => {
    if (promoLink && promoLink?.target || promoLink?.pageFolder?._url || promoLink?.link) {
      const linkTarget = promoLink?.target
        ? promoLink?.target
        : { target: promoLink?.pageFolder?._url || promoLink?.link };

      return (
        <Link link={{ reference: linkTarget }}>
          {children}
        </Link>
      );
    }
    return children;
  }

  const childrenNode = useMemo(() => {
    if (video && isVideo(video)) {
      return (
        <PromoWrapper>
          {inView && (
            <>
              <CustomVideoControls
                hideControls
                className={styles.videoTile}
                videoUrl={video}
                alt={title}
              />
              {promoWritings}
            </>
          )}
        </PromoWrapper>
      )
    }

    if (image) {
      return  (
        <PromoWrapper>
          {isBackgroundImage ? (
            <span
              style={{
                backgroundImage: `url(${image})`,
                backgroundPosition: 'cover',
                backgroundRepeat: 'no-repeat',
              }}
            />
          ) : (
            <Image
              preload={preloadImage}
              className={styles.imageTile}
              image={image}
              alt={title}
            />
          )}
          {promoWritings}
        </PromoWrapper>
      )
    }

    return (
      <PromoWrapper>
        {promoWritings}
      </PromoWrapper>
    )
   }, [image, video, isBackgroundImage, promoLink, title, inView]);

  // calculate position based on layout, cellSpan and position

  const calculatePosition = (layout, cellSpan, position) => {
    if (position < 4 && cellSpan + position >= 4) {

      // case  for 1 row
      return position;
    }
    if (position > 4 && position % layout <= 4) {
      if ((position % layout) - 1 + cellSpan > layout) {
        return 1;
      }
      return position % layout || 1;
    }
    return position;
  };

  // Calculate pormo tile row and position in the row
  const promoPosition = calculatePosition(layout, cellSpan, gridPosition);
  const mergedCellSpan = layout === 2 && cellSpan === 3 ? 4 : cellSpan;

  const getHeight = useCallback(() => {
    if (contentMaxHeight) return contentMaxHeight + '%';
    if (productHeight && promoWrapperRef?.current) {
      const predictedWidth = promoWrapperRef.current.offsetWidth;
      const padding = (productHeight / predictedWidth) * 100;
      if (contentMaxHeight !== padding) {
        setContentMaxHeight(padding);
      }
      return padding + '%';
    }
  }, [promoWrapperRef, contentMaxHeight, productHeight]);

  useLayoutEffect(() => {
    getHeight();
  }, []);

  return (
    <div
      ref={promoWrapperRef}
      data-testid={getHeight()}
      style={{
        '--column-position':
          isMobile && promoPosition > 2
            ? 2
            : promoPosition >= layout
            ? promoPosition % layout === 0
                ? 1
                : promoPosition % layout
            : promoPosition,
        ...textStyles,
      }}
      className={clsx(
        styles.categoryTileWrapper,
        [` ${styles[`layoutShift_${layout}`]}`],
        {
          [` ${styles[`columnWidth_1/4`]}`]:
            mergedCellSpan === 1 || mergedCellSpan === 0,
          [` ${styles[`columnWidth_1/2`]}`]: mergedCellSpan === 2,
          [` ${styles[`columnWidth_3/4`]}`]: mergedCellSpan === 3,
          [` ${styles[`columnWidth_full`]}`]: mergedCellSpan === 4,
        },
      )}
    >
      <div
        className={styles.promoTileInner}
        style={{
          backgroundColor: backgroundColor || 'inherit',
          paddingBottom: contentMaxHeight + '%',
        }}
        ref={ref}
      >
        {React.createElement(
          isClickable ? 'a' : 'span',
          isClickable
            ? {
                className: wrapperStyles,
                href: '/promoPage',
              }
            : {
                className: wrapperStyles,
              },
          childrenNode,
        )}
      </div>
    </div>
  );
};

PromoTile.propTypes = {
  image: PropTypes.string,
  video: PropTypes.string,
  title: PropTypes.string,
  isClickable: PropTypes.bool,
  layout: PropTypes.number,
  backgroundColor: PropTypes.string,
  text: PropTypes.string,
  smallText: PropTypes.string,
  isBackgroundImage: PropTypes.bool,
  imageReference: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.object }),
  ]),
  preloadImage: PropTypes.bool,
};

export default React.memo(PromoTile);
